import { useCallback, useEffect, useState } from 'react';

const scopeKeyFor = (pollId: string, eventId: string | null | undefined) => `${pollId}_${eventId ?? 'none'}`;

/**
 * Which live-poll sheet is open, and the auto-open on a fresh scan: the club
 * announces the poll in the stadium, fans scan, and get the "Vote for your
 * man of the match!" sheet — only when exactly one poll is live, and never
 * for a poll they already voted in.
 *
 * The sheet counts as seen only once the fan CLOSES it (voting is recorded
 * separately as poll_voted_*). It used to be marked the moment it opened, so
 * any reload before the fan acted hid it for the rest of the session — and
 * the app auto-updates by reloading the page a few seconds into the first
 * visit after every deploy (HJK vs Gnistan, 17 Oct 2026: on every phone).
 */
export function usePollAutoOpen({ openPolls, isFreshScan, isDemoSeat, eventId }: {
  openPolls: readonly { id: string }[];
  isFreshScan: boolean;
  isDemoSeat: boolean;
  eventId: string | null | undefined;
}) {
  const [openPollId, setOpenPollId] = useState<string | null>(null);

  useEffect(() => {
    // With several live polls, show the buttons and let the fan choose rather
    // than stacking sheets.
    if (openPolls.length !== 1) return;
    const p = openPolls[0];
    if (!isFreshScan && !isDemoSeat) return; // only right after a scan (demo seats bypass)
    const scopeKey = scopeKeyFor(p.id, eventId);
    if (localStorage.getItem(`poll_voted_${scopeKey}`)) return;
    if (sessionStorage.getItem(`poll_popup_shown_${scopeKey}`)) return;
    setOpenPollId(p.id);
  }, [openPolls.length, openPolls[0]?.id, isFreshScan, isDemoSeat, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPoll = useCallback((id: string) => setOpenPollId(id), []);
  const closePoll = useCallback(() => {
    setOpenPollId((current) => {
      if (current) {
        try {
          sessionStorage.setItem(`poll_popup_shown_${scopeKeyFor(current, eventId)}`, '1');
        } catch {
          // Storage unavailable: it may auto-open once more after a reload.
        }
      }
      return null;
    });
  }, [eventId]);

  return { openPollId, openPoll, closePoll };
}
